import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./FireWatch.css";

const SITE_FILTER = "Piedmont South HOA";

const PiedmontSubMeter = () => {
  const [subMeterJobs, setSubMeterJobs] = useState([]);
  const [propertyAddresses, setPropertyAddresses] = useState([]);
  const [notes, setNotes] = useState([]);
  const [savingKey, setSavingKey] = useState("");

  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);

  // -------------------- HELPERS --------------------
  const formatDate = (date) => {
    if (!date) return "";
    const [y, m, d] = date.split("-");
    return `${m}-${d}-${y}`;
  };

  const monthKeyFromDate = (yyyyMmDd) => {
    if (!yyyyMmDd || typeof yyyyMmDd !== "string") return "";
    return yyyyMmDd.slice(0, 7); // "YYYY-MM"
  };

  const monthLabel = (monthKey) => {
    if (!monthKey) return "";
    const [y, m] = monthKey.split("-");
    const dt = new Date(Number(y), Number(m) - 1, 1);
    return dt.toLocaleString("en-US", { month: "long", year: "numeric" });
  };

  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const flattenReadings = (job) => {
    const all = [];
    Object.values(job.logs || {}).forEach((day) => {
      (day.readings || []).forEach((rd) => all.push(rd));
    });
    all.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    return all;
  };

  const parseMeterReading = (rd) => {
    const base =
  Number(String(rd.usGallonsWhole || "0").replace(/[^0-9]/g, "")) * 1000;
  
    return (
      base +
      toNumber(rd.x100) * 100 +
      toNumber(rd.x10) * 10 +
      toNumber(rd.x1) +
      toNumber(rd.x01) / 10 +
      toNumber(rd.x001) / 100
    );
  };
  
  const daysBetween = (d1, d2) => {
    const a = new Date(d1 + "T00:00:00Z");
    const b = new Date(d2 + "T00:00:00Z");
    return Math.max(
      1,
      Math.floor((b - a) / (1000 * 60 * 60 * 24))
    );
  };
  
  
  const addUsageByMonth = (map, dateStr, gallons) => {
    const mk = monthKeyFromDate(dateStr);
    if (!mk) return;
  
    if (!map[mk]) {
      map[mk] = { monthKey: mk, totalUsed: 0, readings: [] };
    }
  
    map[mk].totalUsed += gallons;
  };
  
  const recalcLogs = (logs, firstReadingDate) => {
  // flatten
  const all = [];
  Object.values(logs || {}).forEach((day) => {
    (day.readings || []).forEach((rd) => all.push({ ...rd }));
  });

  // sort by date asc
  all.sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  let prev = null;

  const rebuilt = {};

  all.forEach((rd) => {
    const prevTotal = prev ? parseMeterReading(prev) : null;
    const currTotal = parseMeterReading(rd);

    const gallonsUsed =
      prev && currTotal > prevTotal
        ? Math.round((currTotal - prevTotal) * 100) / 100
        : 0;

    const updated = {
      ...rd,
      totalReading: currTotal,
      gallonsUsed,
      fromDate: prev?.date || firstReadingDate || "",
      toDate: rd.date,
    };

    if (!rebuilt[rd.date]) {
      rebuilt[rd.date] = { date: rd.date, readings: [] };
    }
    rebuilt[rd.date].readings.push(updated);

    prev = updated;
  });

  return rebuilt;
};


  // -------------------- FETCH --------------------
  useEffect(() => {
    const fetchAll = async () => {
      try {
        // subMeters for Piedmont only
        const subRef = collection(db, "subMeters");
        const subSnap = await getDocs(subRef);
        const subListAll = subSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const subList = subListAll.filter((j) => j.siteName === SITE_FILTER);
        setSubMeterJobs(subList);

        // addresses
        const addrRef = collection(db, "propertiesAddresses");
        const addrSnap = await getDocs(addrRef);
        const addrList = addrSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPropertyAddresses(addrList);

        // notes
        const notesRef = collection(db, "subMeterMonthlyNotes");
        const qNotes = query(notesRef, where("siteName", "==", SITE_FILTER));
        const notesSnap = await getDocs(qNotes);
        const notesList = notesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setNotes(notesList);
      } catch (err) {
        console.error("PiedmontSubMeter fetch error:", err);
      }
    };

    fetchAll();
  }, []);

  // lookup address docs
  const addrById = useMemo(() => {
    return propertyAddresses.reduce((acc, a) => {
      acc[a.id] = a;
      return acc;
    }, {});
  }, [propertyAddresses]);

  // jobs grouped by addressId
  const jobsByAddressId = useMemo(() => {
    const map = {};
    subMeterJobs.forEach((job) => {
      if (!job.addressId) return;
      if (!map[job.addressId]) map[job.addressId] = [];
      map[job.addressId].push(job);
    });

    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => (a.addressLabel || "").localeCompare(b.addressLabel || ""));
    });

    return map;
  }, [subMeterJobs]);

  // address cards
  const addressCards = useMemo(() => {
    const addressIds = Object.keys(jobsByAddressId);

    const cards = addressIds.map((addressId) => {
      const job = jobsByAddressId[addressId][0];
      const addrDoc = addrById[addressId] || null;

      const fullName = addrDoc
        ? `${addrDoc.firstName || ""} ${addrDoc.lastName || ""}`.trim()
        : "";

      const readings = flattenReadings(job);

      const monthMap = {};
for (let i = 1; i < readings.length; i++) {
  const prev = readings[i - 1];
  const curr = readings[i];

  const prevTotal = parseMeterReading(prev);
  const currTotal = parseMeterReading(curr);

  const diff = currTotal - prevTotal;
  if (diff <= 0) {
    console.warn("Meter rollover or bad reading", {
      addressId,
      prev,
      curr,
    });
    continue;
  }  

  const days = daysBetween(prev.date, curr.date);
  const dailyAvg = diff / days;

  let cursor = new Date(prev.date);
  for (let d = 0; d < days; d++) {
    cursor.setDate(cursor.getDate() + 1);
    const iso = cursor.toISOString().slice(0, 10);
    addUsageByMonth(monthMap, iso, dailyAvg);
  }
}

readings.forEach((rd, i) => {
  const mk = monthKeyFromDate(rd.date);
  if (!mk) return;

  if (!monthMap[mk]) {
    monthMap[mk] = { monthKey: mk, totalUsed: 0, readings: [] };
  }

  monthMap[mk].readings.push({
    date: rd.date,
    totalReading: parseMeterReading(rd).toFixed(2),
    gallonsUsed:
      i === 0
        ? ""
        : (parseMeterReading(rd) - parseMeterReading(readings[i - 1])).toFixed(2),
  });
});

// ✅ HANDLE SINGLE-READING CASE (AFTER forEach)
if (readings.length === 1) {
  const mk = monthKeyFromDate(readings[0].date);
  if (mk && !monthMap[mk]) {
    monthMap[mk] = {
      monthKey: mk,
      totalUsed: 0,
      readings: [
        {
          date: readings[0].date,
          totalReading: parseMeterReading(readings[0]).toFixed(2),
          gallonsUsed: "",
        },
      ],
    };
  }
}

      const months = Object.values(monthMap).sort((a, b) =>
        (b.monthKey || "").localeCompare(a.monthKey || "")
      );

      return {
        addressId,
        addressLabel: job.addressLabel || addrDoc?.addressLabel || "",
        fullName,
        months,
      };
    });

    cards.sort((a, b) => (a.addressLabel || "").localeCompare(b.addressLabel || ""));
    return cards;
  }, [jobsByAddressId, addrById]);

  // selected card (NOW correctly outside addressCards)
  const selectedCard = useMemo(() => {
    if (!selectedAddressId) return null;
    return addressCards.find((c) => c.addressId === selectedAddressId) || null;
  }, [selectedAddressId, addressCards]);

  const openPopup = () => {
    if (!selectedAddressId) {
      alert("Please select an address.");
      return;
    }
    setShowDetailsPopup(true);
  };

  const closePopup = () => setShowDetailsPopup(false);

  // Notes helpers
  const noteKey = (addressId, monthKey) => `${addressId}__${monthKey}`;

  const notesByKey = useMemo(() => {
    const map = {};
    notes.forEach((n) => {
      map[noteKey(n.addressId, n.monthKey)] = n;
    });
    return map;
  }, [notes]);

  const handleSaveNote = async ({ addressId, addressLabel, monthKey, value }) => {
    if (!addressId || !monthKey) return;

    const key = noteKey(addressId, monthKey);
    const existing = notesByKey[key];

    try {
      setSavingKey(key);

      if (existing?.id) {
        await updateDoc(doc(db, "subMeterMonthlyNotes", existing.id), {
          note: value || "",
          addressLabel: addressLabel || existing.addressLabel || "",
          updatedAt: new Date(),
        });

        setNotes((prev) =>
          prev.map((n) =>
            n.id === existing.id ? { ...n, note: value || "", updatedAt: new Date() } : n
          )
        );
      } else {
        const payload = {
          siteName: SITE_FILTER,
          addressId,
          addressLabel: addressLabel || "",
          monthKey,
          note: value || "",
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        const docRef = await addDoc(collection(db, "subMeterMonthlyNotes"), payload);
        setNotes((prev) => [...prev, { id: docRef.id, ...payload }]);
      }
    } catch (err) {
      console.error("Save note error:", err);
      alert("Error saving note. Check console.");
    } finally {
      setSavingKey("");
    }
  };

  return (
    <div className="firewatch-page">
      <h1>Piedmont Sub Meter</h1>

      <div style={{ marginBottom: 12 }}>
        <strong>Site Name:</strong> {SITE_FILTER}
      </div>

      {addressCards.length === 0 ? (
        <div style={{ padding: 12 }}>No Sub Meter records found for {SITE_FILTER}.</div>
      ) : (
        <>
          {/* Address Picker */}
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ minWidth: 280 }}>
              <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
                Select Address
              </label>

              <select
                value={selectedAddressId}
                onChange={(e) => setSelectedAddressId(e.target.value)}
                style={{ width: "100%" }}
              >
                <option value="">-- Choose an address --</option>
                {addressCards.map((c) => (
                  <option key={c.addressId} value={c.addressId}>
                    {c.addressLabel}{c.fullName ? ` — ${c.fullName}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <button className="blue-button" onClick={openPopup}>
              View
            </button>
          </div>

          {/* Optional quick summary */}
          {selectedCard && (
            <div style={{ marginTop: 12, padding: 10, border: "1px solid #ddd", borderRadius: 6 }}>
              <strong>Selected:</strong> {selectedCard.addressLabel}
              {selectedCard.fullName ? ` (${selectedCard.fullName})` : ""}
            </div>
          )}

          {/* DETAILS POPUP */}
          {showDetailsPopup && selectedCard && (
            <div className="modal-overlay" onClick={closePopup}>
              <div className="modal" style={{ maxWidth: 1000, width: "95%" }} onClick={(e) => e.stopPropagation()}>
                <h2 style={{ marginBottom: 6 }}>{selectedCard.addressLabel}</h2>

                {selectedCard.fullName ? (
                  <div style={{ marginBottom: 10 }}>
                    <strong>Name:</strong> {selectedCard.fullName}
                  </div>
                ) : null}

                {selectedCard.months.length === 0 ? (
                  <div style={{ padding: 8 }}>No readings yet.</div>
                ) : (
                  selectedCard.months.map((m) => {
                    const key = noteKey(selectedCard.addressId, m.monthKey);
                    const existing = notesByKey[key];
                    const initial = existing?.note || "";

                    return (
                      <div key={m.monthKey} style={{ borderTop: "1px solid #ddd", paddingTop: 10, marginTop: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                          <div>
                            <strong>{monthLabel(m.monthKey)}</strong>
                          </div>
                          <div>
                            <strong>Total Used:</strong> {Math.round(m.totalUsed * 100) / 100} gallons
                          </div>
                        </div>

                        <div style={{ marginTop: 8 }}>
                          <table className="firewatch-table">
                            <thead>
                              <tr>
                                <th>Reading Date</th>
                                <th>Total Reading</th>
                                <th>Gallons Used</th>
                              </tr>
                            </thead>
                            <tbody>
                              {m.readings.map((rd, idx) => (
                                <tr key={idx}>
                                  <td>{formatDate(rd.date)}</td>
                                  <td>{rd.totalReading}</td>
                                  <td>{rd.gallonsUsed}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div style={{ marginTop: 10 }}>
                          <label style={{ display: "block", fontWeight: 700, marginBottom: 6 }}>
                            Notes (editable)
                          </label>

                          <NoteEditor
                            initialValue={initial}
                            onSave={(val) =>
                              handleSaveNote({
                                addressId: selectedCard.addressId,
                                addressLabel: selectedCard.addressLabel,
                                monthKey: m.monthKey,
                                value: val,
                              })
                            }
                            saving={savingKey === key}
                          />
                        </div>
                      </div>
                    );
                  })
                )}

                <div className="modal-buttons" style={{ marginTop: 14 }}>
                  <button className="green-button" onClick={closePopup}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Note editor must be OUTSIDE PiedmontSubMeter
const NoteEditor = ({ initialValue, onSave, saving }) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={3}
        style={{ width: "100%", maxWidth: 900 }}
        placeholder="Add notes for this address/month..."
      />
      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        <button className="blue-button" onClick={() => onSave(value)} disabled={saving}>
          {saving ? "Saving..." : "Save Note"}
        </button>
      </div>
    </div>
  );
};

export default PiedmontSubMeter;
